import { FC, ReactNode } from "react";

/** Page background and padding. Wrap a whole list screen in this. */
export const Page: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="ui-page min-h-full bg-canvas p-4 text-sm text-body md:p-6">{children}</div>
);

export type PageMetaItem = {
	label: string;
	value: ReactNode;
};

/** Screen title block, with optional right-hand meta readouts and actions. */
export const PageHeader: FC<{
	title: ReactNode;
	subtitle?: ReactNode;
	meta?: PageMetaItem[];
	actions?: ReactNode;
}> = ({ title, subtitle, meta, actions }) => (
	<header className="mb-6 flex flex-wrap items-start justify-between gap-6 border-b border-line pb-5">
		<div>
			<h1 className="m-0 text-2xl font-bold text-ink">{title}</h1>
			{subtitle && <p className="mt-1 mb-0 text-sm text-muted">{subtitle}</p>}
		</div>
		<div className="flex flex-wrap items-center gap-8">
			{meta?.map((item) => (
				<div key={item.label} className="flex flex-col gap-0.5">
					<span className="text-[11px] font-semibold uppercase tracking-[0.09em] text-faint">
						{item.label}
					</span>
					<span className="text-[15px] font-bold tabular-nums text-ink">{item.value}</span>
				</div>
			))}
			{actions && <div className="flex items-center gap-2.5">{actions}</div>}
		</div>
	</header>
);

export type StatTone = "blue" | "orange" | "purple" | "green" | "grey";

const TILE_TONE: Record<StatTone, string> = {
	blue: "border-t-brand-blue",
	orange: "border-t-brand-orange",
	purple: "border-t-brand-purple",
	green: "border-t-brand-green",
	grey: "border-t-brand-grey"
};

export const StatTile: FC<{
	label: ReactNode;
	value: ReactNode;
	caption?: ReactNode;
	tone?: StatTone;
}> = ({ label, value, caption, tone = "grey" }) => (
	<article
		className={`flex flex-col gap-1.5 rounded-lg border border-line border-t-[3px] bg-surface px-[1.15rem] pb-[1.15rem] pt-4 ${TILE_TONE[tone]}`}
	>
		<span className="text-[11px] font-bold uppercase tracking-[0.09em] text-muted">{label}</span>
		<span className="text-[2rem] font-bold leading-[1.1] tabular-nums text-ink">{value}</span>
		{caption && <span className="text-[13px] text-faint">{caption}</span>}
	</article>
);

/** Responsive row of StatTiles. */
export const StatTiles: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-4">{children}</div>
);

/** Strip above a grid for search boxes, filters and primary buttons. */
export const Toolbar: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="mb-4 flex flex-wrap items-center justify-between gap-4">{children}</div>
);

/** Uppercase label introducing a block of content. */
export const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
	<h2 className="mb-3 mt-0 text-[11px] font-bold uppercase tracking-[0.09em] text-faint">{children}</h2>
);

export type PageButtonTone = "primary" | "quiet" | "danger";

const BUTTON_TONE: Record<PageButtonTone, string> = {
	primary: "text-white bg-brand-green border-transparent",
	quiet: "text-body bg-surface border-line-strong",
	danger: "text-white bg-brand-red border-transparent"
};

/** Standalone button matching the grid's row actions. */
export const PageButton: FC<{
	tone?: PageButtonTone;
	disabled?: boolean;
	onClick?: () => void;
	children: ReactNode;
}> = ({ tone = "primary", disabled = false, onClick, children }) => (
	<button
		type="button"
		disabled={disabled}
		onClick={onClick}
		className={`cursor-pointer whitespace-nowrap rounded-md border px-3.5 py-2 font-[inherit] text-[13px] font-bold hover:brightness-[0.94] disabled:cursor-default disabled:border-transparent disabled:bg-tone-neutral-bg disabled:text-faint disabled:brightness-100 ${BUTTON_TONE[tone]}`}
	>
		{children}
	</button>
);
